/**
 * RelatedDaysSection Component
 *
 * A reusable React component for displaying related days in accommodation details.
 * Shows clickable day chips that link to the specific day pages where this accommodation appears.
 *
 * Registry-aware (Issue 3): stays_on links live in the connection registry with
 * displayMode `itinerary_entry`, which the generic RelatedEntitiesSection filters out
 * of the accommodation's "Related Entities" list on purpose (so a stay isn't shown twice
 * — once here, once there). On registry-only trips the legacy contentBlocks this
 * component originally read are empty, so without a registry read this section — and the
 * hotel's only surface for its stay days — was blank. Day ids are read from both sources
 * and de-duplicated; the days array (already loaded by AccommodationDetails) is the single
 * source of truth for date/number formatting either way.
 *
 * Expected Props:
 * @prop {Object} accommodation - The accommodation data
 * @prop {Array} accommodation.contentBlocks - Legacy content block references (may be empty)
 * @prop {string} tripId - Trip ID for navigation links
 * @prop {string} userId - effectiveUserId, for registry reads
 * @prop {Array} days - Array of all day objects for the trip (each with id and date)
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { linkPresentationService, eventBridgeService, EVENT_TYPES } from '@trip-planner-monorepo/shared';

const RelatedDaysSection = React.memo(({ accommodation, tripId, userId, days }) => {
  const accommodationId = accommodation?.id;
  const canReadRegistry = Boolean(userId && tripId && accommodationId);
  const [registryDayIds, setRegistryDayIds] = useState([]);
  const [refreshVersion, setRefreshVersion] = useState(0);

  const loadRegistryStays = useCallback(async () => {
    if (!canReadRegistry) {
      setRegistryDayIds([]);
      return;
    }
    try {
      const { models } = await linkPresentationService.getRelatedEntityModels(
        userId,
        tripId,
        'accommodation',
        accommodationId,
        { surface: 'itinerary' }
      );
      setRegistryDayIds((models || []).filter((m) => m.entityType === 'day').map((m) => m.entityId));
    } catch (error) {
      console.error('[RelatedDaysSection] Failed to load registry stays:', error);
      setRegistryDayIds([]);
    }
  }, [canReadRegistry, userId, tripId, accommodationId]);

  useEffect(() => {
    loadRegistryStays();
  }, [loadRegistryStays, refreshVersion]);

  useEffect(() => {
    if (!canReadRegistry) {
      return undefined;
    }
    const linkRefreshEvents = [EVENT_TYPES.LINK_CREATED, EVENT_TYPES.LINK_UPDATED, EVENT_TYPES.LINK_REMOVED];
    const subscriptions = linkRefreshEvents.map((eventType) =>
      eventBridgeService.subscribe(eventType, (payload) => {
        if (payload?.tripId && payload.tripId !== tripId) {
          return;
        }
        setRefreshVersion((v) => v + 1);
      })
    );
    return () => subscriptions.forEach((subscription) => subscription.unsubscribe());
  }, [canReadRegistry, tripId]);

  const legacyDayIds = (accommodation?.contentBlocks || [])
    .filter((block) => block.type !== 'document_reference' && block.dayId)
    .map((block) => block.dayId);

  const dayIds = Array.from(new Set([...registryDayIds, ...legacyDayIds]));

  if (dayIds.length === 0 || !days || days.length === 0) {
    return null;
  }

  return (
    <div className="related-days-section">
      <h3>Related Days</h3>
      <div className="day-chips">
        {dayIds.map((dayId) => {
          const dayIndex = days.findIndex((day) => day.id === dayId);
          if (dayIndex === -1) return null; // skip if not found
          const dayNumber = dayIndex + 1;
          const date = new Date(days[dayIndex].date);
          // Format date as 'Sun 3 Aug 2025' (no leading zero)
          const dayOfWeek = format(date, 'EEE');
          const dayOfMonth = date.getDate();
          const monthYear = format(date, 'MMM yyyy');
          const formattedDate = `${dayOfWeek} ${dayOfMonth} ${monthYear}`;
          return (
            <Link
              key={dayId}
              to={`/trips/${tripId}/day/${dayId}`}
              className="day-chip"
            >
              {`Day ${dayNumber} - ${formattedDate}`}
            </Link>
          );
        })}
      </div>
    </div>
  );
});

// Add display name for better debugging
RelatedDaysSection.displayName = 'RelatedDaysSection';

export default RelatedDaysSection;
